import { useCallback, useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import type { ProfileImageProps } from '~/components/atoms/ProfileImage/ProfileImage'
import ProfileImage, {
  ProfileImageType,
} from '~/components/atoms/ProfileImage/ProfileImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useSliderContext } from '~/contexts/slideOutsContext'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import { commaSeperateName } from '~/utils/commaSeperateName'
import {
  getFirstLetter,
  getFirstWord,
  getLastWord,
  sortArrayKeyByFirstWord,
} from '~/utils/sortArrayKeyByFirstWord'
import { sortArrayKeyByLastWords } from '~/utils/sortArrayKeyByLastWord'

import styles from './Catalogue.module.css'
import RecordTypes = indexes.RecordTypes

export interface CatalogueProps {
  items: ProfileImageProps[]
  renderImages?: boolean
  type?: ProfileImageType
  onClick?: (term: string) => void
  customClickHandler?: any
  insightsQueryID?: string
  className?: string
  showNumberOfWorks?: boolean
  testID?: string
  searchInsightsEvent?: TSearchAnalystEventProps
  recordType?: RecordTypes
  isActiveConditional?: string
  shallowOverride?: boolean
  closeParentMenu?: () => void | null
  orderBySurname?: boolean
  multipleActive?: any[]
  testAttrItems?: string
}

export interface ProcessedProps {
  section?: string
  items?: ProfileImageProps[]
}

export default function Catalogue({
  items,
  renderImages,
  type = ProfileImageType.Artist,
  showNumberOfWorks = true,
  onClick,
  insightsQueryID,
  className,
  testID,
  searchInsightsEvent,
  isActiveConditional,
  recordType,
  shallowOverride,
  closeParentMenu,
  orderBySurname = false,
  customClickHandler,
  multipleActive,
  testAttrItems,
}: CatalogueProps) {
  const { closeMobileNavigation } = useSliderContext()

  // Sort the items by the last word of the artist name (either surname or 'banksy' style names)

  const sortedItems = orderBySurname
    ? (sortArrayKeyByLastWords(items, 'name') as ProfileImageProps[])
    : (sortArrayKeyByFirstWord(items, 'name') as ProfileImageProps[])

  const router = useRouter()

  const processedData: any = []

  let lastSectionIndex = 0

  // Process the data into the format we need.
  sortedItems?.map(item => {
    // Get first letter of surname/alias
    const sectionFirstLetter = orderBySurname
      ? getFirstLetter(getLastWord(item.name)?.toLowerCase())
      : getFirstLetter(getFirstWord(item.name)?.toLowerCase())

    if (
      !processedData.some(function (obj: any) {
        return obj['section'] === sectionFirstLetter
      })
    ) {
      const itemsObj = [] as any

      sortedItems.map((item, idx) => {
        const itemFirstLetter = orderBySurname
          ? getFirstLetter(getLastWord(item.name)?.toLowerCase())
          : getFirstLetter(getFirstWord(item.name)?.toLowerCase())
        if (sectionFirstLetter === itemFirstLetter) {
          itemsObj.push({ ...item, index: idx + 1 })
        }
      })

      // New letter so create the section
      processedData[lastSectionIndex] = {
        section: sectionFirstLetter,
        items: itemsObj,
      }
      lastSectionIndex++
    }
  })

  const catalogueSections = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
  ]

  const refs = useRef<HTMLDivElement[]>([])
  const [links, setLinks] = useState([])
  useEffect(() => {
    const newLinks: any = []
    catalogueSections.map(title => {
      const hasRef = processedData.findIndex(
        (section: any) =>
          section.section?.toLowerCase() === title?.toLowerCase()
      )
      newLinks.push(
        <button
          key={title}
          className={styles.catalogueLink}
          onClick={() => {
            onClick?.(title)
            refs.current[hasRef].scrollIntoView({ behavior: 'smooth' })
          }}
          data-testid={testID && `catalougue-link-${testID}`}
          disabled={hasRef > -1 ? false : true}
        >
          <Text size={TextSizes.primaryTitleSml}>{title}</Text>
        </button>
      )
    })
    setLinks(newLinks)
  }, [refs])

  const getOverrideUrl = (item: any) => {
    if (recordType === RecordTypes.ARTICLES) {
      return isActiveConditional === item.slug
        ? '/articles'
        : `/artist-${item.slug}/articles`
    }

    return undefined
  }

  const checkActive = (item: any) => {
    if (multipleActive) {
      return (
        multipleActive.find((match: any) => match.id == item._id)?.checked ||
        false
      )
    }
    return isActiveConditional && isActiveConditional === item.slug
      ? true
      : false
  }

  const onFilterClick = useCallback(
    (id: string, slug?: string) => {
      customClickHandler(id, slug)
    },
    [multipleActive]
  )

  return (
    <nav
      className={classnames(styles.catalogueWrapper, className && className)}
      data-testid={testID}
    >
      <div className={styles.catalogueLinks}>{links.map(item => item)}</div>
      {processedData.map((cat: ProcessedProps) => {
        return (
          <div
            key={cat.section}
            ref={ref => {
              refs.current.push(ref as HTMLDivElement)
            }}
            data-testid={testID && `section-${testID}`}
            className={styles.section}
          >
            <Text
              Tag="h4"
              size={TextSizes.primaryTitleMed}
              className={styles.sectionTitle}
            >
              {cat.section}
            </Text>

            {cat.items?.map((item, idx) => (
              <ProfileImage
                key={`${item.name}-${idx}`}
                {...item}
                testAttr={testAttrItems}
                name={
                  orderBySurname ? commaSeperateName(item?.name) : item.name
                }
                showNumberOfWorks={showNumberOfWorks}
                type={type}
                inline={true}
                renderImage={renderImages}
                countType={
                  recordType === RecordTypes.ARTICLES ? 'articles' : undefined
                }
                totalArtworks={
                  recordType === RecordTypes.ARTICLES
                    ? item.totalArticles
                    : item.totalArtworks
                }
                isActive={checkActive(item)}
                overrideUrl={getOverrideUrl(item)}
                clickHandler={e => {
                  // shallow change the url and prevent linking off
                  // used on the articles to dynamically filter by url
                  if (shallowOverride) {
                    e.preventDefault()
                    const url = getOverrideUrl(item) as string
                    router.push(url, undefined, {
                      shallow: true,
                    })
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                  if (customClickHandler) {
                    onFilterClick(item._id, item.slug)

                    e.preventDefault()
                  }
                  // Close the mobile menu through context too as the catalogue can be inside the sub nav of artworks.
                  closeMobileNavigation?.()
                  closeParentMenu?.()
                }}
                className={styles.catalogueItem}
                smallImage={true}
                insightsQueryID={insightsQueryID}
                searchInsightsEvent={searchInsightsEvent}
                recordType={recordType}
                noPrefetch
                fallBackToInitials
              />
            ))}
          </div>
        )
      })}
    </nav>
  )
}
